import { useState, useRef, useCallback, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ToolPageLayout from '../components/ToolPageLayout';
import { useToolGenerate } from '../hooks/useToolGenerate';
import { pickPhotoFromLibrary } from '@/utils/nativePhoto';

const EXPAND_RATIO = 0.35;

async function buildOutpaintCanvas(imageDataUrl: string, directions: string[]): Promise<string> {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageDataUrl;
  });

  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const topPad = directions.includes('top') ? Math.round(sourceHeight * EXPAND_RATIO) : 0;
  const bottomPad = directions.includes('bottom') ? Math.round(sourceHeight * EXPAND_RATIO) : 0;
  const leftPad = directions.includes('left') ? Math.round(sourceWidth * EXPAND_RATIO) : 0;
  const rightPad = directions.includes('right') ? Math.round(sourceWidth * EXPAND_RATIO) : 0;

  const canvas = document.createElement('canvas');
  canvas.width = sourceWidth + leftPad + rightPad;
  canvas.height = sourceHeight + topPad + bottomPad;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not available');

  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, leftPad, topPad, sourceWidth, sourceHeight);
  return canvas.toDataURL('image/png');
}

export default function OutpaintPage() {
  const { t } = useTranslation();
  const [image, setImage] = useState<string | null>(null);
  const [directions, setDirections] = useState<Array<'top' | 'bottom' | 'left' | 'right'>>(['top']);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    isGenerating,
    result,
    error,
    doGenerate,
    doRegenerate,
    doCancel,
    clearError,
    user,
    pointsCost,
    controls,
  } = useToolGenerate('outpaint');

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImage(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  const toggleDirection = (dir: 'top' | 'bottom' | 'left' | 'right') => {
    setDirections((prev) => {
      if (prev.includes(dir)) {
        if (prev.length === 1) return prev;
        return prev.filter((d) => d !== dir);
      }
      return [...prev, dir];
    });
  };

  const canGenerate = !!image && directions.length > 0;

  const handleGenerate = () => {
    if (!image || directions.length === 0) return;
    void buildOutpaintCanvas(image, directions)
      .then((expandedImage) => {
        doGenerate('', expandedImage, undefined, { outpaintDirections: directions });
      })
      .catch(() => {
        doGenerate('', image ?? undefined, undefined, { outpaintDirections: directions });
      });
  };

  const dirOptions: Array<{ id: 'top' | 'bottom' | 'left' | 'right'; icon: string; label: string }> = [
    { id: 'top', icon: 'ri-arrow-up-line', label: t('outpaint_top') },
    { id: 'bottom', icon: 'ri-arrow-down-line', label: t('outpaint_bottom') },
    { id: 'left', icon: 'ri-arrow-left-line', label: t('outpaint_left') },
    { id: 'right', icon: 'ri-arrow-right-line', label: t('outpaint_right') },
  ];

  return (
    <ToolPageLayout
      title={t('extool_outpaint')}
      icon="ri-aspect-ratio-fill"
      iconBg="linear-gradient(135deg, #8B5CF6, #A78BFA)"
      iconColor="#8B5CF6"
      description={t('extool_outpaint_desc')}
      pointsCost={pointsCost}
      onGenerate={handleGenerate}
      isGenerating={isGenerating}
      canGenerate={canGenerate}
      result={result}
      error={error}
      onRegenerate={doRegenerate}
      onCancel={doCancel}
      onClearError={clearError}
      controls={controls}
    >
      {/* Upload image */}
      <div
        className="rounded-3xl p-4 mb-3"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,107,157,0.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-5 h-5 rounded-md flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)' }}
          >
            <i className="ri-image-add-line text-white text-xs" />
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('outpaint_uploadLabel')}</span>
          <span className="text-[10px] text-red-400 ml-auto">{t('gen_required')}</span>
        </div>

        {image ? (
          <div
            className="relative w-full rounded-2xl overflow-hidden mb-3"
            style={{ aspectRatio: '16/9', border: '1px solid rgba(255,107,157,0.15)' }}
          >
            <img src={image} alt="upload" className="w-full h-full object-contain" />
            <button
              onClick={() => setImage(null)}
              className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-gray-800/60 text-white cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => pickPhotoFromLibrary(fileInputRef, setImage)}
            className="w-full rounded-2xl flex flex-col items-center justify-center gap-2 cursor-pointer active:scale-[0.98] transition-transform"
            style={{
              aspectRatio: '16/9',
              background: 'rgba(139,92,246,0.08)',
              border: '2px dashed rgba(139,92,246,0.25)',
            }}
          >
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(139,92,246,0.12)' }}
            >
              <i className="ri-upload-cloud-2-line text-2xl text-violet-400" />
            </div>
            <p className="text-sm text-gray-500">{t('outpaint_uploadPlaceholder')}</p>
            <p className="text-xs text-gray-400">{t('img2img_supportFormat')}</p>
          </button>
        )}
      </div>

      {/* Direction selection */}
      <div
        className="rounded-3xl p-4 mb-3"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,107,157,0.12)',
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div
            className="w-5 h-5 rounded-md flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)' }}
          >
            <i className="ri-expand-diagonal-line text-white text-xs" />
          </div>
          <span className="text-xs font-semibold text-gray-500">{t('outpaint_directionLabel')}</span>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {dirOptions.map((dir) => {
            const active = directions.includes(dir.id);
            return (
              <button
                key={dir.id}
                onClick={() => toggleDirection(dir.id)}
                className={`flex flex-col items-center gap-1 py-3 rounded-xl cursor-pointer transition-all active:scale-95 ${
                  active ? 'bg-violet-50' : 'bg-gray-50 hover:bg-gray-100'
                }`}
                style={active ? { border: '1px solid rgba(139,92,246,0.2)' } : {}}
              >
                <i className={`${dir.icon} text-lg ${active ? 'text-violet-500' : 'text-gray-400'}`} />
                <span className={`text-xs ${active ? 'text-violet-600 font-medium' : 'text-gray-500'}`}>
                  {dir.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
    </ToolPageLayout>
  );
}
